import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import siteConfig from '../data/siteConfig.json';
import { Link } from 'react-router-dom';

const Services = () => {
  const { t } = useLanguage();

  const servicesList = [
    { key: 'replacement', img: siteConfig.images.services.replacement },
    { key: 'repair', img: siteConfig.images.services.repair },
    { key: 'side', img: siteConfig.images.services.side },
    { key: 'rear', img: siteConfig.images.services.rear },
    { key: 'sunroof', img: siteConfig.images.services.sunroof },
    { key: 'insurance', img: siteConfig.images.services.insurance },
  ];

  return (
    <div className="bg-slate-50 min-h-screen pb-24">
      {/* Page Header */}
      <div className="bg-glass-dark text-white py-20">
        <div className="container mx-auto px-6 max-w-7xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('services.title')}</h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">{t('services.subtitle')}</p>
        </div>
      </div>

      <div className="container mx-auto px-6 max-w-7xl mt-16">
        <div className="space-y-20">
          {servicesList.map((service, index) => (
            <motion.div 
              key={service.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={`flex flex-col ${index % 2 !== 0 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 items-center bg-white rounded-3xl overflow-hidden shadow-sm border border-slate-100`}
            >
              <div className="w-full lg:w-1/2 h-64 lg:h-96">
                <img 
                  src={service.img} 
                  alt={t(`services.items.${service.key}.title`)} 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-full lg:w-1/2 p-8 lg:p-12">
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  {t(`services.items.${service.key}.title`)}
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  {t(`services.items.${service.key}.desc`)}
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-slate-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-glass-blue flex-shrink-0" /> Guaranteed Workmanship
                  </li>
                  <li className="flex items-center gap-3 text-slate-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-glass-blue flex-shrink-0" /> Fast Turnaround
                  </li>
                </ul>
                <Link to="/contact" className="inline-flex items-center bg-slate-900 hover:bg-glass-blue text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                  {t('common.getQuote')}
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        <section className="mt-20 md:mt-28">
          <div className="text-center mb-12">
            <div className="inline-flex px-4 py-2 bg-glass-blue/10 text-glass-blue rounded-full text-sm font-bold mb-5">
              {t('garageServices.badge')}
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-5">{t('garageServices.title')}</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">{t('garageServices.subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {[
              t('garageServices.trustSignals')[0],
              t('garageServices.trustSignals')[1],
              t('garageServices.trustSignals')[2]
            ].map((signal) => (
              <div key={signal} className="bg-white border border-slate-100 rounded-2xl p-5 flex items-start gap-3 shadow-sm">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-glass-blue flex-shrink-0" />
                <p className="text-sm font-semibold text-slate-700 leading-relaxed">{signal}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 md:p-8 min-w-0"
            >
              <h3 className="text-2xl font-bold text-slate-900 mb-5">{t('garageServices.sections.maintenance.title')}</h3>
              <ul className="space-y-4">
                {t('garageServices.sections.maintenance.items').map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-700">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-glass-blue flex-shrink-0" />
                    <span><strong>{item}</strong> <span className="text-slate-500">({t('garageServices.appointmentNote')})</span></span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.08 }}
              className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 md:p-8 min-w-0"
            >
              <h3 className="text-2xl font-bold text-slate-900 mb-3">{t('garageServices.sections.tires.title')}</h3>
              <p className="text-slate-600 leading-relaxed mb-6">{t('garageServices.sections.tires.desc')}</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {t('garageServices.sections.tires.sizes').map((size) => (
                  <span key={size} className="px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg text-sm font-semibold text-slate-700">
                    {size}
                  </span>
                ))}
              </div>
              <p className="text-sm font-bold text-glass-blue">{t('garageServices.payAtGarage')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.16 }}
              className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 md:p-8 min-w-0"
            >
              <h3 className="text-2xl font-bold text-slate-900 mb-5">{t('garageServices.sections.hardware.title')}</h3>
              <ul className="space-y-4">
                {t('garageServices.sections.hardware.items').map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-700">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-glass-blue flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                * {t('garageServices.sections.hardware.restriction')}
              </p>
            </motion.div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact" className="inline-flex w-full sm:w-auto items-center justify-center bg-glass-blue hover:bg-blue-600 text-white px-8 py-4 rounded-lg font-bold transition-colors shadow-lg">
              {t('common.bookAppointment')}
            </Link>
            <Link to="/contact" className="inline-flex w-full sm:w-auto items-center justify-center bg-slate-900 hover:bg-slate-800 text-white px-8 py-4 rounded-lg font-bold transition-colors shadow-lg">
              {t('common.requestQuote')}
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Services;
